/**
 * homeIdeaPipeline
 *
 * Pure grouping/sorting logic for the /home control command. The Overview
 * panel surfaces a client's *active* ideas (everything except COMPLETE and
 * REJECTED) bucketed by where they sit in the 3-stage pipeline
 * (Concept -> Media -> Posts), so the SMM sees what needs attention.
 *
 * No React, no data fetching: ideas in, view-model out. The stage-node
 * derivation itself is reused from ``ideaStageState`` so home and the idea
 * workspace page speak the same language. Media item counts are not carried
 * on the idea list payload, so the media stage is inferred from whether the
 * idea already has linked posts (posts imply produced media). Exact counts
 * stay the job of the idea workspace page.
 */

import { Idea, IdeaStatus } from "../Ideas/types";
import { IDEA_STATUS_CONFIG } from "../Ideas/ideaStatusConfig";
import {
  DeriveStagesInput,
  MediaCounts,
  StageId,
  summarizePostsProgress,
} from "../Ideas/ideaStageState";

/** Statuses that take an idea out of the active pipeline. */
const TERMINAL_STATUSES: ReadonlySet<IdeaStatus> = new Set<IdeaStatus>([
  "COMPLETE",
  "REJECTED",
]);

/** Pipeline order used for both grouping and rendering. */
export const ACTIVE_STAGE_ORDER: readonly StageId[] = [
  "concept",
  "media",
  "posts",
];

/** Section heading shown for each pipeline bucket on the overview. */
export const STAGE_GROUP_LABELS: Record<StageId, string> = {
  concept: "Concept",
  media: "Media",
  posts: "Posts",
};

/** One-line cue describing what the bucket is waiting on. */
export const STAGE_GROUP_HINTS: Record<StageId, string> = {
  concept: "Being shaped or awaiting approval",
  media: "Approved — ready to produce",
  posts: "In production across linked posts",
};

/** An idea is active while it is neither completed nor rejected. */
export function isActiveIdea(idea: Idea): boolean {
  return !TERMINAL_STATUSES.has(idea.status);
}

/**
 * Build the stage-node derivation input from a list-payload idea. Media
 * counts are absent on the list, so they are inferred: an idea with linked
 * posts is treated as having media, otherwise none.
 */
export function deriveIdeaStageInput(idea: Idea): DeriveStagesInput {
  const posts = summarizePostsProgress(idea.posts);
  const media: MediaCounts =
    posts.total > 0
      ? { photo: 0, video: 0, audio: 0, total: posts.total }
      : { photo: 0, video: 0, audio: 0, total: 0 };
  return { status: idea.status, media, posts };
}

/**
 * Which pipeline bucket an idea currently sits in.
 *
 * - Not yet approved (DRAFT / PENDING / REJECTED) -> ``concept``.
 * - Approved/ready/complete with no linked posts -> ``media`` (ready to produce).
 * - Approved/ready/complete with linked posts -> ``posts`` (in production).
 */
export function currentStageOf(idea: Idea): StageId {
  if (
    idea.status === "APPROVED" ||
    idea.status === "READY" ||
    idea.status === "COMPLETE"
  ) {
    const posts = summarizePostsProgress(idea.posts);
    return posts.total > 0 ? "posts" : "media";
  }
  return "concept";
}

/** Newest-first by last write, falling back to creation time. */
function recencyKey(idea: Idea): number {
  const ts = idea.updatedAt || idea.createdAt;
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Sort a list of ideas newest-first without mutating the input. */
export function sortByRecency(ideas: Idea[]): Idea[] {
  return [...ideas].sort((a, b) => recencyKey(b) - recencyKey(a));
}

export interface IdeaStageGroup {
  stage: StageId;
  label: string;
  hint: string;
  ideas: Idea[];
}

/**
 * Bucket active ideas by pipeline stage, in pipeline order, newest-first
 * within each bucket. Empty buckets are dropped so the overview only shows
 * stages that actually hold work.
 */
export function groupActiveIdeasByStage(ideas: Idea[]): IdeaStageGroup[] {
  const active = ideas.filter(isActiveIdea);
  return ACTIVE_STAGE_ORDER.map((stage) => ({
    stage,
    label: STAGE_GROUP_LABELS[stage],
    hint: STAGE_GROUP_HINTS[stage],
    ideas: sortByRecency(active.filter((i) => currentStageOf(i) === stage)),
  })).filter((group) => group.ideas.length > 0);
}

export interface ActiveIdeasSummary {
  /** Count of active (non-terminal) ideas. */
  active: number;
  /** Active ideas awaiting client approval (PENDING). */
  awaitingApproval: number;
  /** Active ideas still in draft. */
  drafts: number;
  /** Active ideas with at least one linked post. */
  inProduction: number;
}

/** High-signal counts for the overview header. */
export function summarizeActiveIdeas(ideas: Idea[]): ActiveIdeasSummary {
  const active = ideas.filter(isActiveIdea);
  return {
    active: active.length,
    awaitingApproval: active.filter((i) => i.status === "PENDING").length,
    drafts: active.filter((i) => i.status === "DRAFT").length,
    inProduction: active.filter((i) => currentStageOf(i) === "posts").length,
  };
}

/** Statuses the pipeline panel filters by, in tab order. */
export const PIPELINE_STATUS_ORDER = [
  "DRAFT",
  "PENDING",
  "APPROVED",
  "READY",
] as const;

export type PipelineFilterId = "ALL" | (typeof PIPELINE_STATUS_ORDER)[number];

export interface PipelineFilterOption {
  id: PipelineFilterId;
  label: string;
  count: number;
}

/**
 * Filter options for the pipeline panel: All first, then one tab per idea
 * status (labels from IDEA_STATUS_CONFIG, the single source). Empty
 * statuses keep their tab with count 0 — the tab set is stable, never
 * count-gated (DD-007). Filtering is by status, not pipeline stage: the
 * per-row stage rail already shows where each idea sits.
 */
export function derivePipelineFilters(ideas: Idea[]): PipelineFilterOption[] {
  const active = ideas.filter(isActiveIdea);
  return [
    { id: "ALL", label: "All", count: active.length },
    ...PIPELINE_STATUS_ORDER.map((status) => ({
      id: status as PipelineFilterId,
      label: IDEA_STATUS_CONFIG[status].label,
      count: active.filter((i) => i.status === status).length,
    })),
  ];
}

export function filterPipelineIdeas(
  ideas: Idea[],
  filter: PipelineFilterId
): Idea[] {
  const active = ideas.filter(isActiveIdea);
  if (filter === "ALL") return active;
  return active.filter((i) => i.status === filter);
}
